import { useId } from 'react'

import type { FirstPartyTrafficReport, FirstPartyTrafficRow } from '@/api/firstPartyTraffic'
import { formatInteger, SegmentedControl } from '@/design'

import { channelBarValue, formatChannelBarValue, METRIC_OPTIONS, type ChannelBarRow, type ChartMetric } from './channelBarMetric'
import { isNotTracked, NOT_TRACKED_EXPLANATION, NOT_TRACKED_LABEL, notTrackedLast } from './dataQuality'
import './channel-bar-chart.css'

export type { ChannelBarRow }

/** A bar's share of the longest, in percent: a positive value is never thinner than 1%, zero or none is no bar. */
function barWidth(value: number | null, top: number): number {
  if (value === null || value <= 0 || top <= 0) return 0
  return Math.max(1, (value / top) * 100)
}

/** The Not tracked bar's key: no channel's key can be it (channel keys are the API's slugs). */
export const NOT_TRACKED_KEY = '(not tracked)'

/** Channels largest first by the plotted metric, the Not tracked bar always last. */
function ordered(rows: readonly ChannelBarRow[], metric: ChartMetric): ChannelBarRow[] {
  const value = (row: ChannelBarRow) => channelBarValue(row, metric) ?? -1
  return notTrackedLast(
    [...rows].sort((a, b) => value(b) - value(a)),
    (row) => Boolean(row.notTracked),
  )
}

/** The bar's hover title: its figures, and for Not tracked that it is no channel's. */
function barTitle(row: ChannelBarRow, value: string): string {
  const visits = row.notTracked ? 'no tracked visit' : `${formatInteger(row.sessions)} sessions`
  return `${row.label}: ${value} · ${formatInteger(row.orders)} orders · ${visits}`
}

/**
 * One horizontal bar per channel, plotting revenue, orders or conversion rate from
 * a `ChannelBarRow[]` — the single data shape both the Traffic page's first-party
 * report and the Overview's channel widget adapt into (see `channelBarRowsFromReport`
 * below). Channels are one series in the design system's accent; the caption names
 * the metric, and the caller's `caption` names the source and model. The Not tracked
 * bar, when the report has one, is drawn neutral and last, and a legend says which is
 * which. Values sit beside the bars in text ink; each bar's full figures are its hover title.
 */
export function ChannelBarChart({
  rows,
  metric,
  onMetricChange,
  currency,
  caption,
}: {
  rows: readonly ChannelBarRow[]
  metric: ChartMetric
  onMetricChange: (next: ChartMetric) => void
  currency: string
  caption?: string
}) {
  const captionId = useId()
  const notTrackedId = useId()
  const label = METRIC_OPTIONS.find((option) => option.value === metric)?.label ?? metric
  const channels = ordered(rows, metric)
  const hasNotTracked = channels.some((row) => row.notTracked)
  const top = Math.max(0, ...channels.map((row) => channelBarValue(row, metric) ?? 0))
  const format = (value: number | null) => formatChannelBarValue(metric, value, currency)

  return (
    <figure className="cbc-chart" aria-labelledby={captionId}>
      <div className="cbc-chart__head">
        <figcaption id={captionId}>
          {label} by channel{caption ? ` · ${caption}` : ''}
        </figcaption>
        <SegmentedControl label="Chart metric" options={METRIC_OPTIONS} value={metric} onChange={onMetricChange} />
      </div>
      {channels.length === 0 ? (
        <p className="cbc-note">No channels to chart in this period.</p>
      ) : (
        <ol className="cbc-chart__bars" aria-label={`${label} by channel`}>
          {channels.map((row) => {
            const value = channelBarValue(row, metric)
            return (
              <li
                key={row.key}
                className="cbc-chart__row"
                data-channel={row.key}
                data-not-tracked={row.notTracked || undefined}
                aria-describedby={row.notTracked ? notTrackedId : undefined}
                title={barTitle(row, format(value))}
              >
                <span className="cbc-chart__label">{row.label}</span>
                <span className="cbc-chart__track" aria-hidden="true">
                  <span className="cbc-chart__bar" style={{ width: `${barWidth(value, top)}%` }} />
                </span>
                <span className="cbc-chart__value">{format(value)}</span>
              </li>
            )
          })}
        </ol>
      )}
      {hasNotTracked ? (
        <ul className="cbc-legend" aria-label="Legend">
          <li>
            <span className="cbc-legend__swatch" aria-hidden="true" />
            Tracked channels
          </li>
          <li data-not-tracked title={NOT_TRACKED_EXPLANATION}>
            <span className="cbc-legend__swatch" aria-hidden="true" />
            {NOT_TRACKED_LABEL}: orders with no tracked visit
            <span id={notTrackedId} className="ci-visually-hidden">
              {NOT_TRACKED_EXPLANATION}
            </span>
          </li>
        </ul>
      ) : null}
    </figure>
  )
}

/**
 * A `level=channel` first-party report, mapped into `ChannelBarChart`'s row shape —
 * the only place the traffic report shape is mapped for this chart. The Not tracked
 * row (`credit: 'unattributed'`) becomes the neutral bar, labelled "Not tracked"; from
 * an API older than the coverage card the null-key row (no touch, its credit unknown)
 * is dropped, as it always was.
 */
export function channelBarRowsFromReport(report: FirstPartyTrafficReport): ChannelBarRow[] {
  return report.rows.flatMap((row): ChannelBarRow[] => {
    const figures = { revenueMinor: row.revenueMinor, orders: row.orders, conversionRate: row.conversionRate, sessions: row.sessions }
    if (isNotTracked(row)) return [{ key: NOT_TRACKED_KEY, label: NOT_TRACKED_LABEL, notTracked: true, ...figures }]
    return hasChannel(row) ? [{ key: row.key, label: row.label, ...figures }] : []
  })
}

function hasChannel(row: FirstPartyTrafficRow): row is FirstPartyTrafficRow & { key: string } {
  return row.key !== null
}
